import { useState } from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Tooltip,
    IconButton,
    Tabs,
    Tab,
    Badge,
    Alert,
} from '@mui/material';
import {
    SimilarityRequirementDetails,
    UrsRequirementsSimilarityValidationResult,
} from '../../urstemplates/ursTemplatesSlice';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { SimpleEditUrsRequirementDialog } from './SimpleEditUrsRequirementDialog';
import { URSRequirement } from '../UrsSlice';
import { SimpleDeleteUrsRequirementDialog } from './SimpleDeleteUrsRequirementDialog';
import { Localized } from '@fluent/react';

interface SimilarityResultsPanelProps {
    results: UrsRequirementsSimilarityValidationResult[];
    onRequirementUpdated: (updated: URSRequirement) => void;
    onRequirementDeleted: (deletedId: string) => void;
}

type AlgoTab = 'sbert' | 'levenshtein';

export const SimilarityResultsPanel: React.FC<SimilarityResultsPanelProps> = ({
    results,
    onRequirementUpdated,
    onRequirementDeleted
}) => {
    const [selectedRequirementId, setSelectedRequirementId] = useState<string | number | null>(
        null,
    );
    const [activeTab, setActiveTab] = useState<AlgoTab>('sbert');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<URSRequirement | null>(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<URSRequirement | null>(null);
    const [deleteType, setDeleteType] = useState<{ typeCode: string; typeName: string } | null>(null);

    const getDetails = (
        r: UrsRequirementsSimilarityValidationResult,
    ): SimilarityRequirementDetails[] => {
        if (activeTab === 'sbert') {
            return r.sbertSimilarityDetails ?? [];
        }
        return r.levenshteinSimilarityDetails ?? [];
    };

    const getDetailsCount = (r: UrsRequirementsSimilarityValidationResult) => {
        const sbertCount = r.sbertSimilarityDetails?.length ?? 0;
        const levenshteinCount = r.levenshteinSimilarityDetails?.length ?? 0;

        return {
            sbert: sbertCount,
            levenshtein: levenshteinCount,
            total: sbertCount + levenshteinCount,
        };
    };



    return (
        <Box mt={1}>
            <Stack direction="row" spacing={2}>
                {/* LEWA STRONA – lista wymagań */}
                <Box
                    flex={1}
                    sx={{
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            tableLayout: 'fixed',
                            width: '100%',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '30%' }}>
                                    <Localized id="similarity-validation-number-type">
                                        <span>
                                            _type
                                        </span>
                                    </Localized>
                                </TableCell>
                                <TableCell sx={{ width: '50%' }}>
                                    <Localized id="urs-requirement-req">
                                        <span>
                                            _requirement
                                        </span>
                                    </Localized>
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ width: '10%', whiteSpace: 'nowrap' }}
                                >
                                    S
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ width: '10%', whiteSpace: 'nowrap' }}
                                >
                                    L
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results
                                .map(r => {
                                    const allDetailsCount = getDetailsCount(r);
                                    return (
                                        <TableRow
                                            key={r.requirementId}
                                            hover
                                            selected={r.requirementId === selectedRequirementId}
                                            onClick={() => setSelectedRequirementId(r.requirementId)}
                                            sx={theme => ({
                                                cursor: 'pointer',
                                                '&.Mui-selected': {
                                                    backgroundColor: theme.palette.action.selected,
                                                    outline: `1.5px solid ${theme.palette.success.main}`,
                                                    outlineOffset: -1,
                                                }
                                            })}
                                        >
                                            {/* Nr / Typ */}
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {r.number}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {r.typeCode} – {r.typeName}
                                                </Typography>
                                            </TableCell>

                                            {/* Wymaganie – dangerous + zawijanie */}
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: r.requirementName,
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell align="right">
                                                {allDetailsCount.sbert}
                                            </TableCell>
                                            <TableCell align="right">
                                                {allDetailsCount.levenshtein}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </Box>

                {/* PRAWA STRONA – szczegóły dopasowań */}
                <Paper
                    elevation={1}
                    sx={{
                        flex: 2,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '69vh',

                    }}
                >
                    {(() => {
                        const selectedResult = results.find(
                            r => r.requirementId === selectedRequirementId,
                        );
                        const details = selectedResult ? getDetails(selectedResult) : [];
                        const countsForSelected = selectedResult
                            ? getDetailsCount(selectedResult)
                            : { sbert: 0, levenshtein: 0, total: 0 };

                        const handleSourceEdit = () => {
                            if (!selectedResult) return;
                            setEditTarget(selectedResult.requirementDetail);
                            setEditDialogOpen(true);
                        };
                        const handleSourceDelete = () => {
                            if (!selectedResult) return;

                            setDeleteTarget(selectedResult.requirementDetail);
                            setDeleteType({
                                typeCode: selectedResult.typeCode,
                                typeName: selectedResult.typeName,
                            });
                            setDeleteDialogOpen(true);
                        };

                        const handleDetailRowEdit = (d: SimilarityRequirementDetails) => {
                            if (!selectedResult) return;
                            setEditTarget(d.requirementDetail);
                            setEditDialogOpen(true);
                        };
                        const handleDetailRowDelete = (d: SimilarityRequirementDetails) => {
                            if (!selectedResult) return;

                            setDeleteType({
                                typeCode: d.typeCode,
                                typeName: d.typeName,
                            });
                            setDeleteTarget(d.requirementDetail);
                            setDeleteDialogOpen(true);

                        };

                        if (!selectedResult) {
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    <Localized id="similarity-validation-choose-requirement">
                                        <span>
                                            _choose
                                        </span>
                                    </Localized>
                                </Typography>
                            );
                        }

                        return (
                            <>


                                {/* BLOK WYMAGANIA ŹRÓDŁOWEGO */}
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: theme => `1px solid ${theme.palette.success.light}`,
                                        backgroundColor: theme => theme.palette.action.hover,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="overline" display="block" gutterBottom>
                                            <Localized id="similarity-validation-source-requirement">
                                                <span>
                                                    _source
                                                </span>
                                            </Localized>
                                        </Typography>

                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            {selectedResult.number} –{' '}
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: selectedResult.requirementDetail.name,
                                                }}
                                            />
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 0.5 }}
                                        >
                                            {selectedResult.typeCode} – {selectedResult.typeName}
                                        </Typography>
                                    </Box>

                                    {/* AKCJE DLA WYMAGANIA ŹRÓDŁOWEGO */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Tooltip title={<Localized id="edit">_edit</Localized>}>
                                            <IconButton size="small" onClick={handleSourceEdit}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={<Localized id="delete">_delete</Localized>}>
                                            <IconButton size="small" onClick={handleSourceDelete}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Taby algorytmu */}
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, v) => setActiveTab(v)}
                                    variant="fullWidth"
                                    sx={{ mb: 1 }}
                                >
                                    <Tab
                                        value="sbert"
                                        label={
                                            <Badge
                                                color="primary"
                                                badgeContent={countsForSelected.sbert}
                                                max={99}
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        right: -12, // im większa wartość, tym dalej w prawo
                                                        top: 2,
                                                    },
                                                }}
                                            >
                                                SBERT
                                            </Badge>
                                        }
                                    />
                                    <Tab
                                        value="levenshtein"
                                        label={
                                            <Badge
                                                color="primary"
                                                badgeContent={countsForSelected.levenshtein}
                                                max={99}
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        right: -12,
                                                        top: 2,
                                                    },
                                                }}
                                            >
                                                Levenshtein
                                            </Badge>
                                        }
                                    />
                                </Tabs>


                                {/* NAGŁÓWEK „Podobne wymagania” */}
                                <Box
                                    sx={{
                                        mb: 1.5,
                                        py: 0.5,
                                        px: 1,
                                        textAlign: 'center',
                                        borderRadius: 1,
                                        bgcolor: theme => theme.palette.grey[200],
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        <Localized id="similarity-validation-similar-requirements">_edit</Localized>
                                        ({activeTab === 'sbert' ? 'SBERT' : 'Levenshtein'})
                                    </Typography>
                                </Box>

                                {/* LISTA PODOBNYCH WYMAGAŃ */}
                                <Table size="small" stickyHeader sx={{ maxHeight: '40vh' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '30%' }}>
                                                <Localized id="similarity-validation-number-type">
                                                    <span>
                                                        _type
                                                    </span>
                                                </Localized>
                                            </TableCell>
                                            <TableCell sx={{ width: '50%' }}>
                                                <Localized id="urs-requirement-req">
                                                    <span>
                                                        _requirement
                                                    </span>
                                                </Localized>
                                            </TableCell>
                                            <TableCell align="right">%</TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {details.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Alert
                                                        severity="warning"
                                                        sx={{ m: 1, textAlign: 'center' }}
                                                    >
                                                        <Localized id="similarity-validation-no-results-warning">
                                                            <span>
                                                                _no_similar_requirements
                                                            </span>
                                                        </Localized>
                                                    </Alert>
                                                </TableCell>
                                            </TableRow>

                                        ) : (
                                            details.map(d => (
                                                <TableRow key={d.requirementId}>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {d.number}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {d.typeCode} – {d.typeName}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: d.requirementDetail.name,
                                                            }}
                                                        />
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {d.similarityPercent.toFixed(1)}%
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <Tooltip title={<Localized id="edit">_edit</Localized>}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDetailRowEdit(d)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={<Localized id="delete">_delete</Localized>}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDetailRowDelete(d)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </>
                        );
                    })()}
                </Paper>
            </Stack>

            {editDialogOpen && editTarget && (
                <SimpleEditUrsRequirementDialog
                    requirement={editTarget}
                    onSuccess={updatedRow => {
                        setEditDialogOpen(false);
                        setEditTarget(null);
                        onRequirementUpdated(updatedRow);
                    }}
                    onCancel={() => {
                        setEditDialogOpen(false);
                        setEditTarget(null);
                    }}
                />
            )}
            {deleteDialogOpen && deleteTarget && deleteType && (
                <SimpleDeleteUrsRequirementDialog
                    entity={deleteTarget}
                    typeCode={deleteType.typeCode}
                    typeName={deleteType.typeName}
                    onSuccess={() => {
                        setDeleteDialogOpen(false);
                        setDeleteTarget(null);
                        setDeleteType(null);
                        onRequirementDeleted(deleteTarget.id);
                    }}
                    onCancel={() => {
                        setDeleteDialogOpen(false);
                        setDeleteTarget(null);
                        setDeleteType(null);
                    }}
                />
            )}
        </Box>
    );
};
